import React, { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import NewsGridSkeleton from "@/src/components/companies/NewsGridSkeleton";
import axios from "axios";
import { Pagination } from "@heroui/pagination";

const NewsGrid = ({ ticker }) => {
  const [news, setNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchNews = async () => {
      setIsLoading(true);
      try {
        const response = await axios.post(
          "/api/company/news",
          {
            ticker: ticker,
            page: currentPage,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (Array.isArray(response.data)) {
          setNews(response.data);
          // Assuming the total number of pages is returned in the response
          setTotalPages(response.totalPages || 1);
        } else {
          console.error("Data is not an array", response.data);
        }
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (ticker) {
      fetchNews();
    } else {
      setIsLoading(false);
    }
  }, [ticker, currentPage]);

  // Function to generate a SEO-friendly URL slug
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  if (isLoading) {
    return <NewsGridSkeleton />;
  }

  return (
    <>
      {Array.isArray(news) && news.length !== 0 && (
        <section
          id="news"
          className="p-4 mt-5 bg-white shadow-md rounded-lg border"
        >
          <h2 className="text-xl ml-2 font-bold mb-3">News</h2>
          <ul className="space-y-4">
            {news.map((item, index) => (
              <li
                key={`${item.symbol}-${item.publishedDate}-${index}`}
                className="flex flex-col-row md:flex-row sm:flex-row justify-center items-center bg-white p-4 border-b"
                style={{ minHeight: "150px" }} // Set a minimum height to reduce layout shift
              >
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Source: {item.site}</p>
                  <h3
                    onClick={() => window.open(item.url, "_blank")}
                    className="text-sm lg:text-lg md:text-lg sm:text-sm line-clamp-2 font-bold hover:text-blue-600"
                  >
                    {item.title}
                  </h3>
                  <p className="line-clamp-3 font-light  overflow-hidden my-2 max-h-[4.5em] hidden sm:block">
                    {item.text}
                  </p>
                  <p className="text-sm pt-2 text-gray-500">
                    Published on:{" "}
                    {new Date(item.publishedDate).toLocaleDateString()}
                  </p>
                </div>
                {item.image && (
                  <div className="image-container md:w-1/3 sm:w-1/3 w-1/3 md:pl-5 sm:pl-5 pt-5 md:pt-0 mb-4 sm:pt-0">
                    <Image
                      src={item.image}
                      alt={`Thumbnail image representing the news titled  ${item.title}`}
                      unoptimized
                      width={180}
                      height={96}
                      className="rounded-lg "
                      blurDataURL="/images/BlurBG.webp"
                      priority={true}
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
          {news.length > 0 && (
            <div className="flex justify-center mt-5">
              <Pagination
                loop
                showControls
                color="primary"
                total={totalPages}
                initialPage={1}
                page={currentPage}
                onChange={(page) => setCurrentPage(page)}
                getItemAriaLabel={(type, page, selected) => {
                  switch (type) {
                    case "prev":
                      return "Go to previous page";
                    case "next":
                      return "Go to next page";
                    case "page":
                      return selected
                        ? `Page ${page}, current page`
                        : `Go to page ${page}`;
                    default:
                      return `Pagination item ${page}`;
                  }
                }}
              />
            </div>
          )}
        </section>
      )}
    </>
  );
};

export default NewsGrid;
